"use client";

import { AlertCircle, Wifi, Save, Lock, Clock, Dot } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addData, loadData } from "../../config/data";
import { formatToLocalDate } from "../../config/helper";

function InfoRow({ icon, title, text }) {
    return (
        <div className="flex flex-1 items-center gap-3 bg-[#F0F2F480] h-15 md:h-20 p-3 rounded-lg">
            <div className="h-8 md:h-12 w-8 md:w-12 p-2 md:p-3 rounded-full flex items-center justify-center bg-[#EA24271A]">
                {icon}
            </div>
            <div>
                <p className="text-[11px] md:text-sm text-gray-500">
                    {title}
                </p>
                <h2 className="text-[12px] md:text-xl text-gray-900">{text}</h2>
            </div>
        </div>
    );
}

function Point({ text }) {
    return (
        <div className="flex items-center gap-1">
            <Dot className="text-red-500" />
            <p className="text-[12px] md:text-sm text-gray-500">
                {text}
            </p>
        </div>
    );
}

export default function SectionPage({ setCurrentScreen, onPretestCompleted, pretestToken }) {
    const [assessmentData, setAssessmentData] = useState(null);
    const [sectionData, setSectionData] = useState(null);
    const effectRun = useRef(false);
    const [currenSection, setCurrenSection] = useState(null);

    useEffect(() => {
        const sectionNumber = sessionStorage.getItem("sectionNumber");
        const assessmentSession = sessionStorage.getItem("assessmentLocal");
        const number = Number(sectionNumber);
        setCurrenSection(number);
        if (effectRun.current) return;
        effectRun.current = true;

        if (assessmentSession) {

            if (!sectionNumber) {
                sessionStorage.setItem("sectionNumber", "0");
                const assessmentLocal = sessionStorage.getItem("assessmentLocal");
                if (assessmentLocal) {
                    const parsedData = JSON.parse(assessmentLocal);
                    if (parsedData?.pretestToken !== pretestToken) {
                        sessionStorage.removeItem("assessmentLocal");
                        sessionStorage.removeItem("sectionNumber");
                        navigate(`/${lang}/my-application`);
                        return;
                    }
                    setAssessmentData(parsedData)
                    const sessionData = parsedData.sections[0];
                    setSectionData(sessionData);
                }
            } else {
                const number = Number(sectionNumber);
                const assessmentLocal = sessionStorage.getItem("assessmentLocal");
                if (assessmentLocal) {
                    const index = number + 1
                    sessionStorage.setItem("sectionNumber", `${index}`);
                    const parsedData = JSON.parse(assessmentLocal);
                    if (parsedData?.pretestToken !== pretestToken) {
                        sessionStorage.removeItem("assessmentLocal");
                        sessionStorage.removeItem("sectionNumber");
                        navigate(`/${lang}/my-application`);
                        return;
                    }
                    setAssessmentData(parsedData)
                    const sessionData = parsedData.sections[index];
                    setSectionData(sessionData);
                }

            }
        } else {
            if (!pretestToken) {
                navigate(`/${lang}/my-application`);
                return;
            }
            loadData({
                url: `Candidates/Pretest`,
                params: [{ title: "token", value: pretestToken }],
            }).then(candidateResponse => {
                const candidateData = candidateResponse?.data ?? candidateResponse?.Data ?? null;
                const candidateID = Number(candidateData?.candidateID ?? candidateData?.CandidateID);
                if (Number.isNaN(candidateID) || candidateID <= 0) {
                    navigate(`/${lang}/my-application`);
                    return;
                }
                const payload = {
                    candidateID,
                    pretestToken,
                    startTime: formatToLocalDate(`${new Date()}`),
                }
                addData({
                    url: `TestSessions`,
                    reqBody: payload
                }).then(async res => {
                    if (res?.data) {
                        sessionStorage.setItem("sectionNumber", "0");
                        const resData = { ...res?.data, pretestToken };
                        setAssessmentData(resData)
                        const sessionData = resData.sections[0];
                        setSectionData(sessionData);
                        sessionStorage.setItem("assessmentLocal", JSON.stringify(resData));
                    } else if (typeof res === "string") {
                        const message = res.toLowerCase();
                        if (
                            message.includes("completed") ||
                            message.includes("already exists") ||
                            message.includes("only one test session")
                        ) {
                            sessionStorage.removeItem("sectionNumber");
                            sessionStorage.removeItem("assessmentLocal");
                            onPretestCompleted?.();
                            return;
                        }

                        navigate(`/${lang}/my-application`)
                    } else {
                        navigate(`/${lang}/my-application`)
                    }
                })
            })

        }

    }, [])

    const { lang } = useSelector((state) => state.globalReducer);
    const navigate = useNavigate();
    return (
        <div className="min-h-screen w-full bg-white py-16 flex justify-center items-center">
            <div className="max-w-3xl w-full px-6 mb-[80px] mx-auto">

                <h1 className="text-[24px] md:text-[32px] font-bold text-gray-900 mb-3">
                    {sectionData?.categoryName}
                </h1>

                <div className="flex flex-col bg-white rounded-xl shadow-lg border border-[#E5E7EB] p-8 gap-5">
                    <InfoRow
                        icon={<img src="/asset/icon/file_red.svg" />}
                        title='Total Questions'
                        text={sectionData?.totalQuestions}
                    />

                    <InfoRow
                        icon={<Clock className="text-red-500" />}
                        title={'Duration'}
                        text={`${sectionData?.duration} Minutes`}
                    />

                </div>
            </div>

            <div className="fixed bottom-0 w-full bg-white py-6 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
                <div className="max-w-3xl mx-auto px-6 flex justify-end">
                    <button
                        className="group flex flex-row right-0 bg-[#EA2427] text-white border border-[#EA2427]
                        hover:bg-white hover:text-[#EA2427]
                        rounded-full py-1 px-3 transition-[1s] font-bold md:mr-2"
                        onClick={() => {
                            setCurrentScreen(2);
                        }}
                    >
                        <span>Start Pre Assessment</span>
                        <img
                            src="/asset/icon/arrow_right_white.svg"
                            className="ml-3 w-[20px] group-hover:hidden"
                        />
                        <img
                            src="/asset/icon/arrow_right_red.svg"
                            className="ml-3 w-[20px] hidden group-hover:block"
                        />
                    </button>
                </div>
            </div>
        </div>
    );
}
